import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Box, Stack, Typography, Button, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { CustomFilterV2, TableV2 } from '../../../components/v2';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import * as actionsCalendarioFeriado from '../../../store/calendarioFeriado/actions';
import Formulario from './Formulario';
import InvalidarDialog from './InvalidarDialog';

const TIPO_LABEL = {
  NACIONAL: 'Nacional',
  ESTADUAL: 'Estadual',
  MUNICIPAL: 'Municipal',
};

const TIPO_OPTIONS = [
  { value: 'NACIONAL', label: 'Nacional' },
  { value: 'ESTADUAL', label: 'Estadual' },
  { value: 'MUNICIPAL', label: 'Municipal' },
];

const STATUS_OPTIONS = [
  { value: 'VALIDO', label: 'Válido' },
  { value: 'INVALIDO', label: 'Inválido' },
];

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map((uf) => ({ value: uf, label: uf }));

const CalendarioAdmin = ({ invalidarFeriado, revalidarFeriado }) => {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [openInvalidar, setOpenInvalidar] = useState(false);
  const [invalidarRow, setInvalidarRow] = useState(null);
  const [reload, setReload] = useState(false);

  const [localFilter, setLocalFilter] = useState({
    ano: moment().year(),
    uf: null,
    codigoIbge: null,
    tipo: null,
    status: null,
  });

  const apiFilter = useMemo(() => ({ ...localFilter }), [localFilter]);

  const onAdd = () => {
    setSelectedRow(null);
    setOpen(true);
  };

  const onEdit = (rowId, row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const onClose = (isSave) => {
    setOpen(false);
    setSelectedRow(null);
    if (isSave) setReload((r) => !r);
  };

  const rowActions = [
    {
      key: 'invalidar',
      icon: <NotInterestedIcon fontSize="small" sx={{ color: 'error.main' }} />,
      title: 'Invalidar',
      show: (row) => row.status === 'VALIDO',
      onClick: (rowId, row) => {
        setInvalidarRow(row);
        setOpenInvalidar(true);
      },
    },
    {
      key: 'revalidar',
      icon: <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />,
      title: 'Revalidar',
      show: (row) => row.status === 'INVALIDO',
      onClick: (rowId) => {
        revalidarFeriado(rowId, (ok) => {
          if (ok) setReload((r) => !r);
        });
      },
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Gestão de Calendário/Feriados</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`Ano: ${localFilter.ano ?? 'todos'}`} onClick={() => setOpenFilter(true)} />
          <Chip size="small" label={`UF: ${localFilter.uf || 'todas'}`} onClick={() => setOpenFilter(true)} />
          <Chip size="small" label={`Município: ${localFilter.codigoIbge ? localFilter.codigoIbge : 'todos'}`} onClick={() => setOpenFilter(true)} />
          <Chip size="small" label={`Tipo: ${localFilter.tipo ? TIPO_LABEL[localFilter.tipo] : 'todos'}`} onClick={() => setOpenFilter(true)} />
          <Chip size="small" label={`Status: ${localFilter.status || 'todos'}`} onClick={() => setOpenFilter(true)} />
        </Stack>

        <TableV2
          useApi
          fetchUrl="reports/calendario/filter"
          headerActions={[
            { label: 'Novo', onClick: onAdd, startIcon: <AddIcon />, variant: 'contained' },
          ]}
          actions={rowActions}
          columns={[
            { field: 'dataFormatted', headerName: 'Data', width: 120 },
            { field: 'nome', headerName: 'Nome', flex: 1, minWidth: 180 },
            {
              field: 'tipo',
              headerName: 'Tipo',
              width: 130,
              renderCell: ({ row }) => TIPO_LABEL[row.tipo] || row.tipo,
            },
            {
              field: 'municipioUf',
              headerName: 'Município/UF',
              flex: 0.8,
              minWidth: 160,
              renderCell: ({ row }) =>
                row.codigoIbge ? `${row.municipioNome || ''} / ${row.uf || ''}` : (row.uf || '—'),
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 140,
              renderCell: ({ row }) =>
                renderStatus({ value: row.status, label: row.status === 'VALIDO' ? 'Válido' : 'Inválido' }),
            },
            {
              field: 'origem',
              headerName: 'Origem',
              width: 130,
            },
            {
              field: 'atualizadoEm',
              headerName: 'Última alteração',
              flex: 0.8,
              minWidth: 200,
              renderCell: ({ row }) =>
                row.atualizadoEm
                  ? `${moment(row.atualizadoEm).format('DD/MM/YYYY')} — ${row.usuarioNome || '—'}`
                  : '—',
            },
          ]}
          onEdit={onEdit}
          filterData={apiFilter}
          refreshKey={reload}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          fields={[
            { field: 'useAno', md: 6 },
            { field: 'useSearchSelect', key: 'uf', label: 'UF', md: 6, options: UF_OPTIONS },
            { field: 'useMunicipio', md: 12, multiSelect: false, key: 'codigoIbge', nomeKey: 'municipioNome' },
            { field: 'useSearchSelect', key: 'tipo', label: 'Tipo', md: 6, options: TIPO_OPTIONS },
            { field: 'useSearchSelect', key: 'status', label: 'Status', md: 6, options: STATUS_OPTIONS },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={selectedRow?.id || null}
          row={selectedRow}
          open={open}
          onClose={onClose}
        />

        <InvalidarDialog
          open={openInvalidar}
          feriado={invalidarRow}
          onClose={() => { setOpenInvalidar(false); setInvalidarRow(null); }}
          onConfirm={(motivo) => {
            invalidarFeriado(invalidarRow.id, motivo, (ok) => {
              if (ok) setReload((r) => !r);
            });
            setOpenInvalidar(false);
            setInvalidarRow(null);
          }}
        />
      </Stack>
    </Box>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actionsCalendarioFeriado,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(withRouter(CalendarioAdmin));
